import React, { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "reactfire";
import { Spinner } from "react-bootstrap";

const ClaimsContext = createContext({
  claims: {},
  loading: true,
  hasClaim: () => false,
  refreshClaims: () => Promise.resolve({}),
});

let currentClaims = {};

/**
 * Synchronous non-hook function to check claims from anywhere, including outside components.
 * Returns true if the user has the specified claim(s).
 * 
 * @param {string|string[]|Object} claimOrClaims 
 * - string: returns true if claims[claimOrClaims] is truthy.
 * - string[]: returns true if claims contains all listed claims.
 * - Object: returns true if all key-value pairs in the object match claims.
 */
export function hasClaimSync(claimOrClaims) {
  if (typeof claimOrClaims === "string") {
    return !!currentClaims[claimOrClaims];
  }

  if (Array.isArray(claimOrClaims)) {
    return claimOrClaims.every((c) => !!currentClaims[c]);
  }

  if (typeof claimOrClaims === "object" && claimOrClaims !== null) {
    return Object.entries(claimOrClaims).every(
      ([key, val]) => currentClaims[key] === val
    );
  }

  return false;
}

export function ClaimsProvider({ children }) {
  const { data: user, status } = useUser();
  const [claims, setClaims] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (status === "loading") return;

    if (!user) {
      setClaims({});
      currentClaims = {};
      setLoading(false);
      return;
    }

    setLoading(true);
    user
      .getIdTokenResult()
      .then((idTokenResult) => {
        const resolvedClaims = idTokenResult?.claims || {};
        setClaims(resolvedClaims);
        currentClaims = resolvedClaims;
      })
      .catch((err) => {
        console.error("Error fetching ID token result:", err);
        setClaims({});
        currentClaims = {};
      })
      .finally(() => {
        setLoading(false);
      });
  }, [user, status]);

  const refreshClaims = () => {
    if (!user) return Promise.resolve({});
    return user.getIdTokenResult(true).then((idTokenResult) => {
      const resolvedClaims = idTokenResult?.claims || {};
      setClaims(resolvedClaims);
      currentClaims = resolvedClaims;
      return resolvedClaims;
    });
  };

  const hasClaim = (claimOrClaims) => {
    if (!claims) return false;

    if (typeof claimOrClaims === "string") {
      return !!claims[claimOrClaims];
    }

    if (Array.isArray(claimOrClaims)) {
      return claimOrClaims.every((c) => !!claims[c]);
    }

    if (typeof claimOrClaims === "object" && claimOrClaims !== null) {
      return Object.entries(claimOrClaims).every(
        ([key, val]) => claims[key] === val
      );
    }

    return false;
  };

  if (loading && claims === null) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner
          animation="border"
          role="status"
          size="md"
          style={{ color: "#2665be", borderWidth: "2.5px" }}
        />
      </div>
    );
  }

  return (
    <ClaimsContext.Provider value={{ claims, loading, hasClaim, refreshClaims }}>
      {children}
    </ClaimsContext.Provider>
  );
}

export function useClaims() {
  return useContext(ClaimsContext);
}
